// Phone notifications (web push): what is sent and every line of its copy. Each event has its own
// text and its own place to open: the amount, the merchant and the partner's first name (from the
// household link), never a category, card, note or anything else. Payloads are encrypted end to end
// on the way (aes128gcm), but they show on the lock screen; Settings, Notifications says so.
// Shared by the Worker (src/services/push.ts, src/services/notify.ts) and the PWA (Settings,
// Notifications; the links are read once by handleLinkInUrl() in web/src/main.ts, through go()).

/** The kinds a person can turn on or off. Defaults: all on. */
export type PushKind = 'purchases' | 'splits' | 'settle' | 'reminders';
export const PUSH_KINDS: PushKind[] = ['purchases', 'splits', 'settle', 'reminders'];

export const PUSH_KIND_LABEL: Record<PushKind, string> = {
  purchases: 'New purchases to review',
  splits: 'Split requests and answers',
  settle: 'Settle-up notices and disputes',
  reminders: 'Month-end reminders',
};

/**
 * Where a tap opens the app. Each is read once and cleared (handleLinkInUrl() in main.ts).
 * /#review: the review list (Activity, New); /#tx=ID: that purchase; /#accept=ID: the Accept sheet
 * for that request (the same acceptStart() / acceptSheetFor() door as the Requests card);
 * /#splits: the Splits screen; /#overview: Overview (the settle-up notice with Dispute is there).
 */
export const LINK = {
  review: '/#review',
  tx: (id: string) => `/#tx=${id}`,
  accept: (id: string) => `/#accept=${id}`,
  splits: '/#splits',
  overview: '/#overview',
};

/** What a notification carries (encrypted on the way). `tag` makes a repeat replace the last one. */
export interface PushPayload {
  title: string;
  body: string;
  tag: string;
  url: string;
}

const usd = (cents: number) => `$${(Math.abs(cents) / 100).toFixed(2)}`;
const note = (body: string, tag: string, url: string): PushPayload => ({ title: 'Hush', body, tag, url });

// ---- Case 1: an auto-logged charge to review (the hourly sync, one notification per run) ----

/** One charge: "New purchase to review: $18.27 at Amazon"; several from one run: "3 new purchases to review". */
export function purchasesToReview(charges: { id: string; amountCents: number; merchant: string }[]): PushPayload | null {
  if (!charges.length) return null;
  if (charges.length === 1) {
    const c = charges[0];
    return note(`New purchase to review: ${usd(c.amountCents)} at ${c.merchant}`, 'hush-purchases', LINK.tx(c.id));
  }
  return note(`${charges.length} new purchases to review`, 'hush-purchases', LINK.review);
}

// ---- Case 2: a split request for me ----

/** "Tiffany sent you a split: your share $9.13 of $18.27 at Amazon". */
export const splitRequestPush = (from: string, r: { id: string; shareCents: number; fullCents: number; merchant: string }) =>
  note(`${from} sent you a split: your share ${usd(r.shareCents)} of ${usd(r.fullCents)} at ${r.merchant}`, `hush-split-${r.id}`, LINK.accept(r.id));

/** The same purchase asked again at a new total (an edit, or the hourly sync's real charge): "changed", not "new". */
export const splitChangedPush = (from: string, r: { id: string; shareCents: number; fullCents: number; merchant: string }) =>
  note(`${from} changed a split: your share is now ${usd(r.shareCents)} of ${usd(r.fullCents)} at ${r.merchant}`, `hush-split-${r.id}`, LINK.accept(r.id));

// ---- Case 3 and the other answers to my request ----

/** Information only: "Tiffany accepted your split: Amazon, $9.13" (her share). Opens Splits. */
export const splitAcceptedPush = (from: string, r: { id: string; theirShareCents: number; merchant: string }) =>
  note(`${from} accepted your split: ${r.merchant}, ${usd(r.theirShareCents)}`, `hush-split-${r.id}`, LINK.splits);

/** "Tiffany declined your split: Amazon. The full $18.27 counts in your budget again." Opens my purchase. */
export const splitDeclinedPush = (from: string, r: { id: string; fullCents: number; merchant: string; transactionId: string | null }) =>
  note(`${from} declined your split: ${r.merchant}. The full ${usd(r.fullCents)} counts in your budget again.`, `hush-split-${r.id}`, r.transactionId ? LINK.tx(r.transactionId) : LINK.splits);

// ---- Settle up ----

/**
 * To the other side of a settle up, by who paid: "Neil marked $42.10 as paid by you." or "Neil
 * marked that they paid you $42.10." Opens Overview, where the notice has Dispute.
 */
export function settleMarkedPush(from: string, s: { id: string; netCents: number; payerIsMe: boolean; disputeDays: number }): PushPayload {
  const what = s.netCents === 0 ? `${from} marked your splits as settled.` : s.payerIsMe ? `${from} marked ${usd(s.netCents)} as paid by you.` : `${from} marked that they paid you ${usd(s.netCents)}.`;
  return note(`${what} You can dispute it for ${s.disputeDays} days.`, `hush-settle-${s.id}`, LINK.overview);
}

/** "Tiffany disputed your settle up of $42.10. Those splits are open again." Opens Splits. */
export const settleDisputedPush = (from: string, s: { id: string; netCents: number }) =>
  note(`${from} disputed your settle up${s.netCents ? ` of ${usd(s.netCents)}` : ''}. Those splits are open again.`, `hush-settle-${s.id}`, LINK.splits);

/** Month end, from my side: "Month end: Tiffany owes you $30.00 in splits." Opens Splits. */
export function reminderPush(partner: string, netCents: number): PushPayload {
  const body =
    netCents > 0
      ? `Month end: ${partner} owes you ${usd(netCents)} in splits.`
      : netCents < 0
        ? `Month end: you owe ${partner} ${usd(netCents)} in splits.`
        : `Month end: you have splits with ${partner} to settle.`;
  return note(body, 'hush-reminders', LINK.splits);
}

/** The Settings test, and the fallback for a kind with no event text. */
const TEST_BODY = 'Notifications are working.';
export const pushPayload = (kind: PushKind | 'test'): PushPayload =>
  kind === 'test' ? note(TEST_BODY, 'hush-test', '/') : note('Open Hush to see what changed.', `hush-${kind}`, '/');

/**
 * Push services a subscription may point at. Anything else is refused, so the server never posts
 * to an address someone typed. Apple (web.push.apple.com), Google (FCM), Mozilla and Microsoft.
 */
export const PUSH_HOSTS = [/^web\.push\.apple\.com$/, /^fcm\.googleapis\.com$/, /^updates\.push\.services\.mozilla\.com$/, /^[a-z0-9-]+\.notify\.windows\.com$/];

export function pushEndpointAllowed(endpoint: string): boolean {
  try {
    const u = new URL(endpoint);
    return u.protocol === 'https:' && !u.port && PUSH_HOSTS.some((h) => h.test(u.hostname));
  } catch {
    return false;
  }
}

// ---- Settings, Notifications ----------------------------------------------------------------

export const NOTIFY_TITLE = 'Notifications';
export const NOTIFY_INTRO =
  'Get a notification on this phone for new purchases to review, splits with your partner, settle ups and the month end. They show the amount, the store and your partner\'s first name, also on the lock screen.';
export const NOTIFY_NOT_INSTALLED = 'To get notifications on iPhone, add Hush to your Home Screen first: tap Share, then Add to Home Screen, and open it from there.';
export const NOTIFY_DENIED = 'Notifications are blocked for Hush. To allow them, open iPhone Settings, Notifications, Hush, and turn on Allow Notifications.';
export const NOTIFY_UNSUPPORTED = 'This browser cannot show notifications. Hush emails you instead.';
export const NOTIFY_SERVER_OFF = 'Notifications are not set up on this server yet. Hush emails you instead.';
export const NOTIFY_ON = 'On for this device.';
export const NOTIFY_OFF = 'Off for this device. Hush emails you instead.';
export const NOTIFY_FALLBACK = 'When notifications are off, or a phone cannot be reached, Hush sends the email instead. Never both.';
